import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useEffect, useState } from 'react';
import { Card, Col, Container, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { Route, Routes } from 'react-router-dom';

import { getCredits } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import { Credit } from '../../types';
import AnalyticsHeader from '../../widgets/Header';

// const CreateCreditsButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => {
//   return (
//     <Button onClick={onClick} disabled={disabled}>
//       {' '}
//       New Credits
//     </Button>
//   );
// };

export const CREDITS_QUERY_KEY = ['credits'];

const globalFilterFn: FilterFn<Credit> = (row, columnId, filterValue) => {
  const customerName = row.original.customer?.name?.toString().toLowerCase();
  const phoneNumber = row.original.customer?.phoneNumber?.toString().toLowerCase();
  const orderNumber = row.original.orderNumber.toLowerCase() ?? '';
  const balance = row.original.balance?.toString().toLowerCase() ?? '';
  const createdBy = row.original.createdBy?.fullName?.toLowerCase() ?? '';
  const searchValue = filterValue.toLowerCase();

  return (
    customerName.includes(searchValue) ||
    phoneNumber.includes(searchValue) ||
    orderNumber.includes(searchValue) ||
    balance.includes(searchValue) ||
    createdBy.includes(searchValue)
  );
};

const columnHelper = createColumnHelper<Credit>();

const columns = [
  columnHelper.accessor('customer.name', {
    header: 'Customer',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),

  columnHelper.accessor('customer.phoneNumber', {
    header: 'Phone Number',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('orderNumber', {
    header: 'Order Number',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('balance', {
    header: 'Balance',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id
  }),
  columnHelper.accessor('outlet.name', {
    header: 'Outlet',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),

  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id
  })
];

const limit = 100;

const CreditsTable = ({ query }: any) => {
  const [page, setPage] = useState(1);
  const compositeQueryKey = query
    ? [...CREDITS_QUERY_KEY, { ...query, page, limit }]
    : [CREDITS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getCredits({ ...query, page, limit })
  });
  const credits = data?.results;

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { count } = data ?? {};

  const totalPages = Math.ceil(count! / limit);

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <AnalyticsHeader
        title="Credits"
        // headerButton={<CreateCreditsButton onClick={() => {}} disabled={true} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : credits && credits.length > 0 ? (
              <>
                <TableList credits={credits} columns={columns} />
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages ?? 0}
                  onPageChange={handlePageChange}
                  nextPage={nextPage ?? null}
                  prevPage={prevPage ?? null}
                />
              </>
            ) : (
              <EmptyTableState message="No credits yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/credits/all',
    element: <CreditsTable />
  }
];

const Credits = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Credits;

interface TableListProps {
  credits: Credit[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<Credit, any>[];
}

const TableList = ({ credits, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const tableInstance = useReactTable({
    data: credits!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Card.Header>
        <InputGroup className="input-group-merge input-group-flush input-group-reverse">
          <Form.Control
            type="search"
            placeholder="Search"
            onChange={e => setGlobalFilter(e.target.value ? e.target.value : undefined)}
          />
          <InputGroup.Text>
            <FeatherIcon icon="search" size="1em" />
          </InputGroup.Text>
        </InputGroup>
      </Card.Header>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <td key={cell.id}>
                      <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
